import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    TextField,
} from '@fluentui/react';
import { formStyles } from 'styles/formStyles';
import moment from 'moment-timezone';
import { ArtikelAttachment, uploadArtikelAttachment } from 'api/artikelAttachment';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const styles = mergeStyleSets({
    dropzone: {
        width: '100%',
        paddingTop: 50,
        paddingBottom: 50,
        border: '3px dashed #CCC',
        textAlign: 'center',
        backgroundColor: '#EEE',
        color: '#BBB',
        fontSize: 16,
        fontWeight: 700,
        cursor: 'pointer',
    },
    fileList: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
});

interface AddArtikelAttachmentsProps {
    artikelNr: string;
    onClose: (refetch: boolean) => void;
}

export const AddArtikelAttachments: React.FC<AddArtikelAttachmentsProps> = ({ artikelNr, onClose }) => {
    const [addArtikelAttachmentText, setAddArtikelAttachmentText] = React.useState<{ [key: string]: string }>({});
    const [addAttachmentSendWithAngebot, setAddAttachmentSendWithAngebot] = React.useState<{ [key: string]: boolean }>(
        {},
    );

    const [saving, setSaving] = React.useState<number | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const { acceptedFiles, getRootProps, getInputProps } = useDropzone({ disabled: saving !== null });

    const saveArtikelAttachments = async () => {
        const datum = moment().toISOString();

        if (artikelNr) {
            let uploadError = false;
            for (let i = 0; i < acceptedFiles.length; i += 1) {
                setSaving(i);
                setError(null);
                const attachment: ArtikelAttachment = {
                    artikelNr,
                    datum,
                    text: addArtikelAttachmentText[acceptedFiles[i].name],
                    file: '',
                    sendWithAngebot: addAttachmentSendWithAngebot[acceptedFiles[i].name],
                    oneDriveUrl: null,
                };

                // eslint-disable-next-line no-await-in-loop
                const result = await uploadArtikelAttachment(acceptedFiles[i], attachment);
                if (!result) {
                    uploadError = true;
                    setError('Fehler beim Speichern von ' + acceptedFiles[i].name);
                    break;
                }
            }

            setSaving(null);

            if (!uploadError) {
                onClose(true);
            }
        }
    };

    return (
        <Dialog
            hidden={false}
            title="Dokumente anhängen"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                layerProps: { eventBubblingEnabled: true },
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1000}
            onDismiss={() => onClose(false)}
        >
            <div {...getRootProps({ className: styles.dropzone })}>
                <input {...getInputProps()} />
                <p>
                    Ziehen Sie mit der Maus eine oder mehrere Dateien in diesen Bereich.
                    <br />
                    Alternativ können Sie auch in diesen Bereich klicken und die Dateien auswählen.
                </p>
            </div>
            <div className={styles.fileList}>
                {acceptedFiles.map((file) => {
                    return (
                        <React.Fragment key={file.name}>
                            <TextField
                                label={'Beschreibung - ' + file.name}
                                value={addArtikelAttachmentText[file.name]}
                                disabled={saving !== null}
                                maxLength={100}
                                onChange={(event, newValue) => {
                                    const newTexts = {
                                        ...addArtikelAttachmentText,
                                        [file.name]: newValue || '',
                                    };

                                    setAddArtikelAttachmentText(newTexts);
                                }}
                            />
                            <div>
                                <Checkbox
                                    className={formStyles.checkboxInsideFormAligned}
                                    label="Mit Angebot versenden"
                                    disabled={saving !== null}
                                    checked={addAttachmentSendWithAngebot[file.name]}
                                    onChange={(event, checked) => {
                                        const newSendWithAngebotList = {
                                            ...setAddAttachmentSendWithAngebot,
                                            [file.name]: !!checked,
                                        };

                                        setAddAttachmentSendWithAngebot(newSendWithAngebotList);
                                    }}
                                />
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            {error && <div className={formStyles.error}>{error}</div>}

            <DialogFooter>
                <DefaultButton disabled={saving !== null} onClick={() => onClose(false)} text="Abbrechen" />
                <PrimaryButton
                    disabled={acceptedFiles.length === 0 || saving !== null}
                    onClick={saveArtikelAttachments}
                    text="Speichern"
                />
            </DialogFooter>
        </Dialog>
    );
};
